import { type LiveSummaryTag, TagChip } from "@storytell/ui/live-summary";
import { TbChevronDown, TbClock, TbCopy, TbDots, TbListDetails, TbTag } from "solid-icons/tb";
import { type Component, For, Index, type JSX, type ParentComponent, Show } from "solid-js";
import { useI18n } from "~/i18n";
import { QUIET_BUTTON, Swap } from "./chief";
import { at, Cursor } from "./parts";

/* The desktop app's Notes tab: a page whose header carries the crumb, the title
   and the tag filter, with the meeting's points under topic headings. Its
   editor can't render outside the app, so this is drawn after
   services/chief/frontend/src/panels/NotesPanel.tsx and goes stale when that
   changes. */

/** A tag in the header's filter, with how many points carry it. */
type NotesPill = { tag: LiveSummaryTag; count: number };

/** A pill picked at `at` by a cursor that starts gliding to it at `cursorAt`. */
type NotesFilter = { tagId: string; at: number; cursorAt: number };

const PILL =
  "flex h-[1.625rem] shrink-0 items-center gap-1 rounded-full border px-2.5 text-xs font-semibold whitespace-nowrap";

const AllPill: Component<{ total: number; on: boolean }> = (props) => {
  const { t } = useI18n();
  return (
    <span
      class={PILL}
      classList={{
        "border-primary-text bg-primary-text text-canvas": props.on,
        "border-primary-border text-primary-text": !props.on,
      }}
    >
      {t("marketing.mock.notes.all")} <span class="tabular-nums opacity-60">{props.total}</span>
    </span>
  );
};

/**
 * The Notes page: the crumb with the Chief and People ink legend, the title,
 * its meta line, the tag filter with By topic and By time, the Summary card
 * when there is one, and the points under it.
 */
export const NotesPage: ParentComponent<{
  project: string;
  title: string;
  meta: string;
  /** Every point on the page, tagged or not. */
  total: number;
  pills: NotesPill[];
  filter?: NotesFilter;
  summary?: string;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class="px-6 @2xl:px-10">
      <header class="flex flex-col gap-1 pt-4 pb-2 text-muted-text">
        <div class="flex items-center gap-2 text-xs font-semibold">
          <span class="truncate text-secondary-text">{props.project}</span>
          <span>/</span>
          <span class="shrink-0">{t("marketing.mock.tab.notes")}</span>
          <span class="flex-1" />
          <span class="hidden items-center gap-1.5 font-normal @2xl:flex">
            <span class="h-0.75 w-3.5 rounded-sm bg-(--chief-ink)" />
            Chief
          </span>
          <span class="ms-1.5 hidden items-center gap-1.5 font-normal @2xl:flex">
            <span class="h-0.75 w-3.5 rounded-sm bg-primary-text" />
            {t("marketing.mock.notes.people")}
          </span>
        </div>
        <h1 class="truncate text-[1.625rem] font-semibold tracking-[-0.022em] text-primary-text">{props.title}</h1>
        <p class="text-xs">{props.meta}</p>
        <div class="notes-tag-pills flex flex-wrap items-center gap-1.5 pt-2">
          <Show when={props.filter} fallback={<AllPill total={props.total} on />}>
            {(filter) => (
              <Swap
                swapAt={filter().at}
                before={<AllPill total={props.total} on />}
                after={<AllPill total={props.total} on={false} />}
              />
            )}
          </Show>
          <For each={props.pills}>
            {(pill) => (
              <Show
                when={props.filter?.tagId === pill.tag.id ? props.filter : undefined}
                fallback={<TagChip tag={pill.tag} count={pill.count} />}
              >
                {(filter) => (
                  <span class="press relative" style={at(filter().at)}>
                    <Swap
                      swapAt={filter().at}
                      before={<TagChip tag={pill.tag} count={pill.count} />}
                      after={<TagChip tag={pill.tag} count={pill.count} selected />}
                    />
                    <Cursor start={filter().cursorAt} />
                  </span>
                )}
              </Show>
            )}
          </For>
          <span class="ms-auto hidden shrink-0 items-center rounded-md bg-muted-border p-0.5 text-xs @2xl:flex">
            <span class="flex h-5 items-center gap-1 rounded bg-tile px-1.5 font-medium text-primary-text shadow-sm">
              <TbListDetails class="size-3.5" aria-hidden="true" />
              {t("marketing.mock.notes.by_topic")}
            </span>
            <span class="flex h-5 items-center gap-1 px-1.5">
              <TbClock class="size-3.5" aria-hidden="true" />
              {t("marketing.mock.notes.by_time")}
            </span>
          </span>
        </div>
      </header>
      <Show when={props.summary}>
        <section class="mb-3 flex flex-col gap-1.5 rounded-lg bg-panel px-3.5 py-2.5 ring-1 ring-primary-border">
          <span class="flex items-center gap-1.5 text-xs font-semibold text-secondary-text">
            <TbChevronDown class="size-3.5" aria-hidden="true" />
            {t("marketing.mock.tab.summary")}
            <span class="font-normal text-muted-text">· {t("marketing.mock.live")}</span>
          </span>
          <p class="text-sm leading-relaxed text-secondary-text">{props.summary}</p>
        </section>
      </Show>
      {props.children}
    </div>
  );
};

/** A topic heading among the points. */
export const NotesTopic: Component<{ text: string; appearAt?: number }> = (props) => (
  <h2
    class="mt-3 mb-0.5 text-lg font-semibold tracking-[-0.012em] text-primary-text"
    classList={{ "msg-in": props.appearAt !== undefined }}
    style={at(props.appearAt ?? 0)}
  >
    {props.text}
  </h2>
);

/**
 * One point in Chief's ink, set in when it's a sub-point, with its tag and
 * owner under it. It arrives at `appearAt`, its tag lands at `tagAt`, and a
 * filter that leaves it out folds it away at `leaveAt`.
 */
export const NotesLine: Component<{
  text: JSX.Element;
  sub?: boolean;
  tag?: LiveSummaryTag;
  owner?: string;
  appearAt?: number;
  tagAt?: number;
  leaveAt?: number;
}> = (props) => (
  <Leaves leaveAt={props.leaveAt}>
    <div
      class="py-1"
      classList={{ "ps-6": props.sub, "msg-in": props.appearAt !== undefined }}
      style={at(props.appearAt ?? 0)}
    >
      <p class="text-base leading-[1.62] text-(--chief-ink)">{props.text}</p>
      <span class="flex items-center gap-2">
        <Show when={props.tag}>
          {(tag) => (
            <span
              class="mt-0.5 inline-flex h-5.5 items-center gap-1 rounded-md ps-1.5 pe-1.75 text-xs font-semibold text-secondary-text"
              classList={{ "pop-in": props.tagAt !== undefined }}
              style={{
                "background-color": `color-mix(in srgb, ${tag().color} 12%, transparent)`,
                ...at(props.tagAt ?? 0),
              }}
            >
              <span class="size-1.5 rounded-full" style={{ "background-color": tag().color }} />
              {tag().name}
            </span>
          )}
        </Show>
        <Show when={props.owner}>
          {(owner) => (
            <span
              class="mt-0.5 inline-flex items-center gap-1.5 text-xs text-secondary-text"
              classList={{ "pop-in": props.tagAt !== undefined }}
              style={at(props.tagAt ?? 0)}
            >
              <span class="grid size-4.5 place-items-center rounded-full bg-panel-hover text-[0.6875rem] font-semibold">
                {owner()[0]}
              </span>
              {owner()}
            </span>
          )}
        </Show>
      </span>
    </div>
  </Leaves>
);

/** Folds its child away at `leaveAt`, closing the slot behind it. */
const Leaves: ParentComponent<{ leaveAt?: number }> = (props) => (
  <Show when={props.leaveAt !== undefined} fallback={props.children}>
    <div class="collapse-out grid grid-rows-[0fr] opacity-0" style={at(props.leaveAt ?? 0)}>
      <div class="min-h-0 overflow-hidden">{props.children}</div>
    </div>
  </Show>
);

/** When the toolbar's beats fall: Turn into… pressed, its menu open, the kind picked; the cursors glide in before each. */
type TurnIntoPlay = { turnCursor: number; turn: number; menu: number; pickCursor: number; pick: number };

/**
 * Points selected in Notes, with the selection's toolbar over them and its
 * Turn into… menu open on `picked`. `play` stages the presses; without it the
 * menu is simply open.
 */
export const SelectedNotes: Component<{
  lines: string[];
  formats: string[];
  picked: number;
  play?: TurnIntoPlay;
  class?: string;
}> = (props) => {
  const { t } = useI18n();
  return (
    <div class={`relative ${props.class ?? ""}`}>
      <div class="absolute inset-s-0 -top-12 z-20 rounded-xl border border-primary-border bg-tile p-1 shadow-xl">
        <div class="flex items-center gap-0.5">
          <span class={QUIET_BUTTON}>
            <TbTag class="size-3.5" aria-hidden="true" /> {t("marketing.mock.notes.tags")}
          </span>
          <span class={QUIET_BUTTON}>
            <TbCopy class="size-3.5" aria-hidden="true" /> {t("marketing.mock.sheet.copy")}
          </span>
          <span class="relative">
            <span class="relative flex" classList={{ press: Boolean(props.play) }} style={at(props.play?.turn ?? 0)}>
              <span class="mac-push flex items-center gap-1 px-2 py-1 text-xs font-semibold text-primary-text">
                {t("marketing.mock.follow.turn_into")}
                <TbChevronDown class="size-3.5" aria-hidden="true" />
              </span>
              <Show when={props.play}>{(play) => <Cursor start={play().turnCursor} />}</Show>
            </span>
            <span
              class="mac-menu absolute inset-s-0 top-full z-20 mt-1 flex min-w-37.5 flex-col"
              classList={{ "card-appear": Boolean(props.play) }}
              style={at(props.play?.menu ?? 0)}
            >
              <Index each={props.formats}>
                {(format, i) => (
                  <span
                    class="mac-menu-item relative"
                    classList={{
                      "bg-(--mac-accent) text-white": i === props.picked,
                      press: i === props.picked && Boolean(props.play),
                    }}
                    style={at(props.play?.pick ?? 0)}
                  >
                    {format()}
                    <Show when={i === props.picked ? props.play : undefined}>
                      {(play) => <Cursor start={play().pickCursor} />}
                    </Show>
                  </span>
                )}
              </Index>
            </span>
          </span>
          <span class={QUIET_BUTTON}>
            <TbDots class="size-4" aria-hidden="true" />
          </span>
        </div>
      </div>
      <div class="rounded-md bg-brand/10 px-1 dark:bg-brand/25">
        <For each={props.lines}>{(line) => <NotesLine text={line} />}</For>
      </div>
    </div>
  );
};
